import { makeArticle, saintRelated } from "./learnFactory";

export const pageSaintHowLongTake = makeArticle({
  slug: "how-long-does-saint-tms-treatment-take",
  title: "How Long Does SAINT® TMS Treatment Take? | Neuro Wellness",
  description:
    "SAINT® TMS takes 5 consecutive days: 10 sessions per day, about 10 minutes each, with 50-minute breaks. Learn the full timeline including evaluation and MRI.",
  canonical: "/learn/how-long-does-saint-tms-treatment-take",
  h1: "How Long Does SAINT® TMS Treatment Take?",
  breadcrumbLabel: "How Long SAINT® Takes",
  shortAnswer:
    "SAINT® TMS treatment takes 5 consecutive days. Each day includes 10 sessions of about 10 minutes each, with roughly 50-minute breaks between sessions, so patients spend most of each day at the clinic. Before the treatment week, a psychiatric evaluation and an fMRI scan are required and scheduled in advance. After the 5-day protocol, follow-up visits monitor response. The total treatment week is 5 days, but the full process — evaluation, MRI, treatment, and follow-up — spans a longer period.",
  keyTakeaways: [
    "SAINT® treatment takes 5 consecutive days.",
    "Each day: 10 sessions of ~10 minutes, with 50-minute breaks.",
    "Patients spend most of each treatment day at the clinic.",
    "A psychiatric evaluation and fMRI are required before treatment.",
    "Follow-up visits monitor response after the 5-day protocol.",
  ],
  sections: [
    {
      heading: "The 5-Day Treatment Week",
      paragraphs: [
        "SAINT® treatment is delivered over 5 consecutive days. Each day includes 10 sessions of about 10 minutes each, separated by roughly 50-minute breaks. With this schedule, patients spend most of each day at the clinic.",
        "The 50-minute breaks are a deliberate part of the protocol — they allow the brain to respond to the stimulation pattern between sessions. Over the full 5 days, this totals 50 sessions.",
      ],
    },
    {
      heading: "Before Treatment: Evaluation and MRI",
      paragraphs: ["Before the treatment week begins, two steps are required:"],
      list: [
        "Psychiatric evaluation — a psychiatrist reviews your diagnosis, medication history, and overall health to confirm eligibility.",
        "Functional MRI (fMRI) — a brain scan maps your individual connectivity to identify the personalized stimulation target.",
      ],
      paragraphsAfter: [
        "These are scheduled before the treatment week and must be completed before the first treatment day. The time between your consultation and the start of treatment depends on scheduling availability and, if applicable, insurance verification.",
      ],
    },
    {
      heading: "After Treatment: Follow-Up",
      paragraphs: [
        "After the 5-day protocol, follow-up visits with your psychiatrist monitor response and discuss a maintenance plan. The 2022 RCT measured outcomes at 4 weeks. A maintenance plan may include medication, psychotherapy, and the possibility of another SAINT® course if needed.",
        "Follow-up frequency depends on individual response and clinical judgment. Some follow-up visits may be done remotely.",
      ],
    },
    {
      heading: "The Full Timeline",
      paragraphs: ["The complete SAINT® process includes:"],
      list: [
        "Consultation and psychiatric evaluation (scheduled in advance).",
        "fMRI scan (scheduled before treatment week).",
        "5-day treatment week (10 sessions per day, most of each day at clinic).",
        "Follow-up visits (starting after treatment, ongoing as needed).",
        "Maintenance plan (medication, therapy, monitoring).",
      ],
    },
    {
      heading: "What This Means for Patients",
      paragraphs: [
        "The treatment week itself is 5 days, but the full process — evaluation, MRI, treatment, and follow-up — spans a longer period. Self-pay patients can often start treatment within days of their consultation. Insurance verification, if applicable, may add time.",
        "If you are planning around work, travel, or other commitments, discuss the full timeline with the Neuro Wellness team during your consultation so you can plan accordingly.",
      ],
      links: [
        {
          to: "/learn/what-to-expect-during-saint-tms",
          text: "See a day-by-day guide to the 5 days of SAINT®",
        },
        { to: "/saint", text: "Learn more about SAINT® at Neuro Wellness" },
        {
          to: "/contactus",
          text: "Request a consultation",
          suffix: " or call 954-827-9793.",
        },
      ],
    },
  ],
  faqs: [
    {
      question: "How long does SAINT® TMS treatment take?",
      answer:
        "SAINT® treatment takes 5 consecutive days. Each day includes 10 sessions of about 10 minutes with 50-minute breaks. Before treatment, a psychiatric evaluation and fMRI are required. After the 5-day protocol, follow-up visits monitor response. The full process spans longer than just the treatment week.",
    },
    {
      question: "How many days do I need to be at the clinic for SAINT®?",
      answer:
        "You need to be at the clinic for 5 consecutive days. Each day includes 10 sessions with 50-minute breaks, so you will spend most of each day at the clinic. Before the treatment week, you also need a psychiatric evaluation and an fMRI, which are scheduled separately.",
    },
    {
      question: "How long is each SAINT® session?",
      answer:
        "Each SAINT® session lasts about 10 minutes. With 10 sessions per day and 50-minute breaks between them, patients spend most of each treatment day at the clinic. The 50-minute breaks allow the brain to respond to the stimulation pattern.",
    },
    {
      question: "How quickly can I start SAINT®?",
      answer:
        "After a psychiatric evaluation confirms eligibility, the next steps are scheduling an fMRI and planning the treatment week. Self-pay patients can often start within days. Insurance verification, if applicable, may take longer. Contact Neuro Wellness to discuss timing.",
    },
    {
      question: "Is SAINT® faster than conventional TMS?",
      answer:
        "Yes, in terms of treatment schedule. SAINT® is completed in 5 days, while conventional TMS takes 4–6 weeks of daily visits. However, SAINT® also requires an fMRI and evaluation beforehand. The total process is still shorter than a conventional TMS course.",
    },
  ],
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],
  relatedLinks: saintRelated,
});
